import { Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import type { FeatureChecker } from 'common/src/features/FeatureChecker';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import { IAccount, IUtility } from 'common/src/types/interfaces';

import addBooleanColumnProps from 'portal/src/components/common/tables/addBooleanColumnProps';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import addNumberColumnProps from 'portal/src/components/common/tables/addNumberColumnProps';
import { masterSubAccountColumn } from 'portal/src/components/common/tables/masterSubColumns';
import { isActiveMeter } from 'portal/src/utils/checkActive';

const buildAccountsColumns = (featureChecker: FeatureChecker): GridColDef[] => {
  return [
    { field: 'number', headerName: 'Account Number', minWidth: 160, flex: 1 },
    {
      field: 'utility',
      headerName: 'Utility',
      minWidth: 100,
      flex: 1,
      valueGetter: (value: IUtility) => value?.name ?? '',
    },
    {
      field: 'serviceStartDate',
      ...addDateColumnProps('Service Start Date', 150),
    },
    {
      field: 'serviceEndDate',
      ...addDateColumnProps('Service End Date', 150),
    },
    {
      field: 'activeMeters',
      valueGetter: (_, row: IAccount) =>
        (row.meters || []).filter((m) => isActiveMeter(m)).length,
      ...addNumberColumnProps('Active Meters', true, 150),
    },
    {
      field: 'accountGroup',
      headerName: 'Account Group',
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'department',
      headerName: 'Department',
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'glCode',
      headerName: 'G/L Code',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'rateSchedule',
      headerName: 'Rate Schedule',
      minWidth: 125,
      flex: 1,
    },
    {
      field: 'facilities',
      headerName: 'Facilities',
      minWidth: 150,
      valueGetter: (_, row: IAccount) => {
        const names = (row.meters ?? [])
          .map((m) => m.parentFacility?.name)
          .sort((a, b) => a.localeCompare(b));
        return Array.from(new Set(names)).join(', ');
      },
      renderCell: (params) => {
        const cell = (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value}
          </div>
        );

        return params?.value && params.value.includes(', ') ? (
          <Tooltip title={params.value}>{cell}</Tooltip>
        ) : (
          cell
        );
      },
    },
    ...(featureChecker.productFeatureEnabled(ProductFeatures.allocations)
      ? [masterSubAccountColumn]
      : []),
    {
      field: 'isActive',
      headerName: 'Deactivated',
      ...addBooleanColumnProps({
        displayLogic: { valueForYes: false, yesIsGreen: false },
        includeTooltip: true,
      }),
    },
  ];
};

export default buildAccountsColumns;
